import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Grid
} from '@mui/material'
import {
  Visibility,
  Unarchive,
  Edit,
  Delete,
  MoreVert,
  Archive as ArchiveIcon,
  LocationOn,
  Refresh
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format, parseISO } from 'date-fns'
import archiveService from '../../../services/archiveService'

const ArchiveList = ({ onEdit, onView, statusFilterProps }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)
  const [archives, setArchives] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedArchive, setSelectedArchive] = useState(null)
  const [openRetrieveDialog, setOpenRetrieveDialog] = useState(false)
  const [retrieveData, setRetrieveData] = useState({
    reason: '',
    requestedBy: '',
    urgency: 'NORMAL'
  })

  // Filters
  const [statusFilter, setStatusFilter] = useState(statusFilterProps ? statusFilterProps : 'ALL')
  const [locationFilter, setLocationFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadArchives()
  }, [page, rowsPerPage, statusFilter, locationFilter, searchQuery])

  const loadArchives = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        location: locationFilter !== 'ALL' ? locationFilter : undefined,
        search: searchQuery || undefined
      }

      const response = await archiveService.getArchives(params)

      if (response.data.success) {
        const archiveData = response.data.data
        const archivesList = archiveData?.docs || archiveData || []
        setArchives(Array.isArray(archivesList) ? archivesList : [])
        setTotalCount(archiveData?.totalDocs || response.data.total || archivesList.length)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load archives', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event, archive) => {
    setAnchorEl(event.currentTarget)
    setSelectedArchive(archive)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedArchive(null)
  }

  const handleRetrieve = async () => {
    try {
      await archiveService.retrieveArchive(selectedArchive._id, retrieveData)
      enqueueSnackbar('Archive retrieved successfully', { variant: 'success' })
      setOpenRetrieveDialog(false)
      setRetrieveData({ reason: '', requestedBy: '', urgency: 'NORMAL' })
      loadArchives()
    } catch (error) {
      enqueueSnackbar('Failed to retrieve archive', { variant: 'error' })
    }
  }

  const getStatusChip = (status) => {
    const config = {
      ARCHIVED: { color: 'primary', label: 'Archived' },
      RETRIEVED: { color: 'success', label: 'Retrieved' },
      PENDING_RETRIEVAL: { color: 'warning', label: 'Pending Retrieval' },
      DESTROYED: { color: 'error', label: 'Destroyed' }
    }

    const { color, label } = config[status] || { color: 'default', label: status }

    return (
      <Chip
        label={label}
        color={color}
        size="small"
      />
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Archived Files ({totalCount})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadArchives}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by file number or patient name..."
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Status</SelectMenuItem>
                <SelectMenuItem value="ARCHIVED">Archived</SelectMenuItem>
                <SelectMenuItem value="RETRIEVED">Retrieved</SelectMenuItem>
                <SelectMenuItem value="PENDING_RETRIEVAL">Pending Retrieval</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                label="Location"
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Locations</SelectMenuItem>
                <SelectMenuItem value="WAREHOUSE_A">Warehouse A</SelectMenuItem>
                <SelectMenuItem value="WAREHOUSE_B">Warehouse B</SelectMenuItem>
                <SelectMenuItem value="OFFSITE">Offsite Storage</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Archives Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Details</TableCell>
              <TableCell>Archive Location</TableCell>
              <TableCell>Archived Date</TableCell>
              <TableCell>Retention Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {archives.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Alert severity="info">
                    No archived files found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              archives.map((archive) => (
                <TableRow key={archive._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {archive.mrdFile?.fileNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {archive.mrdFile?.patientName}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">
                          {archive.location?.name || 'Not specified'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {archive.location?.section} - {archive.location?.shelf}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {archive.archivedAt ? format(parseISO(archive.archivedAt), 'MMM dd, yyyy') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {archive.archivedBy?.name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {archive.retentionPeriod} years
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Until: {archive.retentionEndDate ? format(parseISO(archive.retentionEndDate), 'MMM yyyy') : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {getStatusChip(archive.mrdFile.fileStatus)}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onView ? onView(archive) : null}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip> */}

                      {archive.mrdFile.fileStatus === 'ARCHIVED' && (
                        <Tooltip title="Retrieve">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedArchive(archive)
                              setOpenRetrieveDialog(true)
                            }}
                          >
                            <Unarchive fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, archive)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          onEdit && onEdit(selectedArchive)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Location</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          // Handle view history
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
      </Menu>

      {/* Retrieve Dialog */}
      <Dialog open={openRetrieveDialog} onClose={() => setOpenRetrieveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Retrieve Archive</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Requested By"
              value={retrieveData.requestedBy}
              onChange={(e) => setRetrieveData(prev => ({ ...prev, requestedBy: e.target.value }))}
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Urgency</InputLabel>
              <Select
                value={retrieveData.urgency}
                label="Urgency"
                onChange={(e) => setRetrieveData(prev => ({ ...prev, urgency: e.target.value }))}
              >
                <SelectMenuItem value="LOW">Low</SelectMenuItem>
                <SelectMenuItem value="NORMAL">Normal</SelectMenuItem>
                <SelectMenuItem value="HIGH">High</SelectMenuItem>
                <SelectMenuItem value="URGENT">Urgent</SelectMenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Retrieval"
              value={retrieveData.reason}
              onChange={(e) => setRetrieveData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Enter reason for retrieving this archive..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRetrieveDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRetrieve}>
            Retrieve Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ArchiveList